/**
 * E3-FO-E — "gate new work" screen for an ELC tenant that has not finished
 * setup (design §10). Wraps every ELC route except the setup page itself.
 *
 *   loading        → a quiet placeholder (never a flash of the gate)
 *   complete       → the page
 *   incomplete     → "Finish ELC setup": admins get the checklist + a link to
 *                    the setup page, everyone else is told to ask an admin
 *   fetch failed   → the page (fail open — the backend still enforces)
 */
import type { FC, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Settings } from 'lucide-react';

import { SETUP_PATH } from '../api/elcSetupApi';
import { useElcPermissions } from '../utils/elcPermissions';
import { useElcSetup } from '../utils/useElcSetup';
import SetupChecklist from './SetupChecklist';

const ElcSetupGate: FC<{ children: ReactNode }> = ({ children }) => {
  const { status, setup } = useElcSetup();
  const canManage = useElcPermissions().manageSettings.allowed;

  if (status === 'idle' || status === 'loading') {
    return <div className="elc-admin__loading" role="status" aria-label="Checking ELC setup" />;
  }
  if (status === 'error' || !setup || setup.complete) return <>{children}</>;

  const outstanding = setup.items.filter((i) => !i.done);
  return (
    <div style={{ padding: 16, maxWidth: 720 }}>
      <h1 style={{ margin: '0 0 8px', fontSize: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Settings size={20} aria-hidden /> Finish ELC setup
      </h1>
      {canManage ? (
        <>
          <p style={{ marginTop: 0, fontSize: 13 }}>
            Employee Lifecycle needs a few things configured before it can be used. Until then,
            new access requests and manual workflows are blocked.
          </p>
          <SetupChecklist items={outstanding} />
          <Link className="btn btn--primary" to={SETUP_PATH} style={{ display: 'inline-flex', marginTop: 16 }}>
            Go to ELC setup
          </Link>
        </>
      ) : (
        <p style={{ marginTop: 0, fontSize: 13 }}>
          ELC isn&apos;t set up yet — contact your administrator.
        </p>
      )}
    </div>
  );
};

export default ElcSetupGate;
